/**
 * NATURE SCENERY for the live themes.
 * Owner, 6 Jun 2026: "there should be background scenery where these things
 * happen — different scenery for different animated themes, and the motion
 * should be slow so it doesn't create a headache."
 *
 * Each scene is ONE inline SVG of flat silhouettes — no photographs. Reasons:
 *  - it takes the theme's own colours, so the scene can never clash with the
 *    palette or wash out text the way a photo does;
 *  - it is a few kB instead of a few hundred, and stays sharp on any screen;
 *  - individual layers can be animated (sun, water) while the rock stays still.
 * `preserveAspectRatio="none"` is deliberate: it makes an x% in this
 * 1440-wide viewBox the SAME x% on screen, which is what lets the CSS
 * waterfall column line up exactly with the gap between the SVG cliffs.
 * Silhouettes are irregular, so the stretch is invisible.
 *
 * ── WHY THIS FILE HAS NO SUB-COMPONENTS AND NO .map() ──────────────────────
 * The first version drew each tree with a <Pine/> component and each scene
 * through a shared <Svg>{children}</Svg> wrapper. Every scene rendered
 * completely blank. Cause: the preview instrumentation wraps anything it
 * considers dynamic — children passed through a component, and the output of
 * .map() — in `<span style="display:contents">`. A <span> is legal in HTML but
 * meaningless inside <svg>, so all the geometry ended up inside an unknown
 * element and the browser painted nothing (same wrapper that produces the
 * table hydration warnings noted in iter_515).
 * So: every scene is one self-contained <svg> with LITERAL children, and the
 * repeated shapes are built as path STRINGS by the helpers below, at module
 * load. A whole treeline is one <path>.
 *
 * Colours live in styles/ambience.css, one block per theme, keyed on the
 * `s-*` classes.
 */

const n = (v) => Math.round(v * 10) / 10;

// A pine, three tiers, drawn from its base up so it can be planted on a ridge.
const pine = (x, y, h, w) =>
  `M${n(x - w)} ${n(y)}L${n(x - w * 0.58)} ${n(y - h * 0.34)}L${n(x - w * 0.78)} ${n(y - h * 0.33)}` +
  `L${n(x - w * 0.42)} ${n(y - h * 0.66)}L${n(x - w * 0.58)} ${n(y - h * 0.65)}L${n(x)} ${n(y - h)}` +
  `L${n(x + w * 0.58)} ${n(y - h * 0.65)}L${n(x + w * 0.42)} ${n(y - h * 0.66)}` +
  `L${n(x + w * 0.78)} ${n(y - h * 0.33)}L${n(x + w * 0.58)} ${n(y - h * 0.34)}L${n(x + w)} ${n(y)}Z`;

const disc = (cx, cy, r) =>
  `M${n(cx - r)} ${n(cy)}a${n(r)} ${n(r)} 0 1 0 ${n(r * 2)} 0a${n(r)} ${n(r)} 0 1 0 ${n(-r * 2)} 0Z`;

// A broadleaf tree: trunk plus three overlapping crowns.
const broadleaf = (x, y, r) =>
  `M${n(x - r * 0.1)} ${n(y)}V${n(y - r * 1.5)}H${n(x + r * 0.1)}V${n(y)}Z` +
  disc(x, y - r * 1.9, r) + disc(x - r * 0.82, y - r * 1.45, r * 0.62) +
  disc(x + r * 0.78, y - r * 1.5, r * 0.58);

const pineRow = (xs, y, hs, ws) =>
  xs.map((x, i) => pine(x, y, hs[i % hs.length], ws[i % ws.length])).join('');

const leafRow = (xs, y, rs) => xs.map((x, i) => broadleaf(x, y, rs[i % rs.length])).join('');

// A distant treeline: crowns only, no trunks, and the height wanders. With one
// radius and one baseline it came out as a row of identical lollipops.
const crownRow = (xs, y, rs, dys) => xs.map((x, i) => {
  const r = rs[i % rs.length];
  const yy = y + dys[i % dys.length];
  return disc(x, yy - r * 0.9, r) + disc(x - r * 0.78, yy - r * 0.45, r * 0.6) +
    disc(x + r * 0.72, yy - r * 0.5, r * 0.56);
}).join('');

// A horizontal dash, used for the sun's reflection breaking on the water.
const dash = (cx, y, w, h) =>
  `M${n(cx - w / 2)} ${n(y)}H${n(cx + w / 2)}V${n(y + h)}H${n(cx - w / 2)}Z`;

const RIDGE_XS = [58, 128, 196, 266, 336, 404, 474, 544, 612, 684, 752, 822,
  894, 966, 1036, 1106, 1178, 1248, 1318, 1392];

/* ── SUNRISE — first light behind a mountain range ─────────────────────────── */
const SUNRISE_TREES = broadleaf(188, 784, 30) + broadleaf(268, 792, 19) +
  pine(1206, 784, 112, 36) + pine(1284, 790, 78, 26);

export const SunriseScene = () => (
  <svg className="amb__scene" viewBox="0 0 1440 900" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <radialGradient id="ambSunriseSun" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFF6E6" />
        <stop offset="58%" stopColor="#FFDCAE" />
        <stop offset="100%" stopColor="#F8C083" />
      </radialGradient>
      <radialGradient id="ambSunriseHalo" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFCF96" stopOpacity="0.34" />
        <stop offset="52%" stopColor="#FFCF96" stopOpacity="0.1" />
        <stop offset="100%" stopColor="#FFCF96" stopOpacity="0" />
      </radialGradient>
    </defs>
    {/* 6 Jun 2026: "sunrise — no sun at all". It was there, sitting behind the
        far ridge with only a sliver showing. Now it clears the peaks by a good
        margin and carries a halo, so it reads as the sun rather than as a
        gap between two mountains. */}
    <ellipse className="s-sunhalo" cx="1010" cy="272" rx="250" ry="250" />
    <ellipse className="s-sun" cx="1010" cy="272" rx="104" ry="104" />
    <path className="s-far" d="M0 560 L150 450 L246 520 L372 402 L470 498 L600 394 L742 506 L864 428 L1000 520 L1140 436 L1268 514 L1362 464 L1440 510 L1440 900 L0 900 Z" />
    <path className="s-mid" d="M0 650 L170 590 L316 638 L486 556 L654 632 L818 576 L996 648 L1174 588 L1330 640 L1440 606 L1440 900 L0 900 Z" />
    <path className="s-near" d="M0 774 C190 714 344 788 520 754 C700 720 862 794 1044 766 C1226 738 1344 784 1440 760 L1440 900 L0 900 Z" />
    <path className="s-tree" d={SUNRISE_TREES} />
  </svg>
);

/* ── SNOWFALL — snow-covered range, pine forest, drifts in front ──────────── */
const SNOW_TREELINE = pineRow(RIDGE_XS, 648, [52, 68, 84], [15, 19, 23]);
const SNOW_TREES = pine(146, 812, 196, 56) + pine(242, 826, 132, 40) +
  pine(1298, 818, 172, 50) + pine(1212, 830, 112, 34);

export const SnowScene = () => (
  <svg className="amb__scene" viewBox="0 0 1440 900" preserveAspectRatio="none" aria-hidden="true">
    <path className="s-far" d="M0 520 L140 430 L230 486 L330 396 L430 470 L520 384 L640 478 L760 402 L880 484 L1010 410 L1130 482 L1250 422 L1360 488 L1440 446 L1440 900 L0 900 Z" />
    <path className="s-mid" d="M0 622 C140 576 250 622 380 600 C520 576 620 628 760 608 C900 588 1010 632 1150 610 C1280 590 1360 624 1440 606 L1440 900 L0 900 Z" />
    <path className="s-mid-tree" d={SNOW_TREELINE} />
    <path className="s-near s-drift" d="M0 792 C170 738 330 802 500 774 C680 744 840 808 1020 782 C1200 756 1340 798 1440 776 L1440 1140 L0 1140 Z" />
    <path className="s-tree" d={SNOW_TREES} />
  </svg>
);

/* ── FOG — four ridges fading into the haze ───────────────────────────────── */
const FOG_MID_TREES = pine(330, 620, 96, 28) + pine(396, 628, 64, 20) + pine(1052, 614, 86, 26);
const FOG_TREES = pine(186, 828, 158, 44) + pine(1256, 824, 134, 40) + broadleaf(640, 822, 26);

export const FogScene = () => (
  <svg className="amb__scene" viewBox="0 0 1440 900" preserveAspectRatio="none" aria-hidden="true">
    <path className="s-far" d="M0 500 L180 430 L300 478 L440 410 L580 470 L720 418 L860 480 L1000 422 L1140 482 L1280 432 L1440 484 L1440 900 L0 900 Z" />
    <path className="s-mid" d="M0 600 C160 552 300 604 460 580 C620 556 760 610 920 586 C1080 562 1300 606 1440 582 L1440 900 L0 900 Z" />
    <path className="s-mid-tree" d={FOG_MID_TREES} />
    <path className="s-near" d="M0 704 C180 660 340 710 520 690 C700 670 880 716 1060 696 C1240 676 1360 712 1440 696 L1440 900 L0 900 Z" />
    <path className="s-fore" d="M0 814 C200 770 380 820 560 802 C740 784 920 828 1100 810 C1280 792 1380 820 1440 808 L1440 900 L0 900 Z" />
    <path className="s-tree" d={FOG_TREES} />
  </svg>
);

/* ── SUNSET — the sun going down into the sea ─────────────────────────────── */
/* The reflection is dashes rather than one solid streak: a low sun on water
   never reads as a bar, it breaks up across the swell. Kept sparse and dim —
   an earlier pass had ten bright rows and it looked like a barcode. */
const SUN_GLINTS_A = dash(706, 628, 78, 2) + dash(742, 692, 54, 2) + dash(698, 758, 40, 2) + dash(730, 822, 28, 2);
const SUN_GLINTS_B = dash(748, 660, 106, 2) + dash(692, 726, 84, 2) + dash(736, 790, 62, 2);

export const SunsetScene = () => (
  <svg className="amb__scene" viewBox="0 0 1440 900" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <radialGradient id="ambSunsetSun" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFF3E6" />
        <stop offset="58%" stopColor="#FFD6C0" />
        <stop offset="100%" stopColor="#EFAB9A" />
      </radialGradient>
      {/* Golden Hour uses the same scene on a daylight palette, and a pale
          peach disc disappears on a cream sky — so the day variant gets a
          deeper gold of its own (referenced from ambience.css). */}
      <radialGradient id="ambSunsetSunDay" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFF0C8" />
        <stop offset="56%" stopColor="#F9C567" />
        <stop offset="100%" stopColor="#E79A3A" />
      </radialGradient>
      {/* And a halo, because on a bright page the disc alone is the only thing
          in the scene that has to survive being read through a frosted card.
          Transparent unless a theme fills it, so the night Sunset is unchanged. */}
      <radialGradient id="ambSunsetHaloDay" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFC978" stopOpacity="0.46" />
        <stop offset="50%" stopColor="#FFC978" stopOpacity="0.16" />
        <stop offset="100%" stopColor="#FFC978" stopOpacity="0" />
      </radialGradient>
    </defs>
    <ellipse className="s-sunhalo" cx="720" cy="606" rx="300" ry="300" />
    <ellipse className="s-sun" cx="720" cy="606" rx="84" ry="84" />
    <path className="s-far" d="M96 606 C142 556 186 578 226 606 Z M1180 606 C1236 548 1298 576 1352 606 Z" />
    <rect className="s-water" x="0" y="606" width="1440" height="294" />
    <path className="s-glint" d={SUN_GLINTS_A} />
    <path className="s-glint" d={SUN_GLINTS_B} style={{ animationDelay: '4s' }} />
    <rect className="s-shimmer" x="240" y="676" width="620" height="2" />
    <rect className="s-shimmer" x="820" y="760" width="540" height="2" style={{ animationDelay: '9s' }} />
    <path className="s-fore" d="M0 856 C110 812 214 866 330 862 L340 900 L0 900 Z M1440 844 C1330 806 1226 862 1108 866 L1100 900 L1440 900 Z" />
  </svg>
);

/* ── RAINFALL — a lake at dusk, the moon behind the weather ────────────────
   Rebuilt 6 Jun 2026. The owner liked Foliage and nothing else, and the reason
   is instructive: Foliage is a plain dark backdrop, one soft glow, one
   near-black foreground shape, and a handful of distinct slow-moving things.
   Rain had jagged peaks, a twenty-tree treeline and a screen full of drops —
   a busy diagram. Same recipe as Foliage now: soft hills, the moon, a dark
   lake, reeds, and rain you have to look at to notice. ── */
const RAIN_TREES = pine(268, 646, 92, 26) + pine(322, 652, 62, 19) + pine(1148, 640, 84, 24);
const RAIN_REEDS = 'M40 900 C 52 830 34 786 62 742 M92 900 C 104 842 88 806 118 768 ' +
  'M146 900 C 154 852 140 820 166 790 M1400 900 C 1386 838 1404 796 1378 754 ' +
  'M1344 900 C 1332 848 1348 812 1322 780 M1292 900 C 1284 858 1298 826 1274 800';

export const RainScene = () => (
  <svg className="amb__scene" viewBox="0 0 1440 900" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <radialGradient id="ambRainMoon" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#DCEAF6" stopOpacity="0.42" />
        <stop offset="60%" stopColor="#BBD3E8" stopOpacity="0.2" />
        <stop offset="100%" stopColor="#9FC0DC" stopOpacity="0" />
      </radialGradient>
    </defs>
    <ellipse className="s-sun" cx="1116" cy="236" rx="74" ry="74" />
    <path className="s-far" d="M0 520 C220 470 380 522 600 500 C820 478 1020 530 1240 502 C1342 489 1400 501 1440 496 L1440 900 L0 900 Z" />
    <path className="s-mid" d="M0 644 C200 598 420 650 640 628 C860 606 1080 654 1300 632 C1382 623 1420 631 1440 627 L1440 900 L0 900 Z" />
    <path className="s-mid-tree" d={RAIN_TREES} />
    <rect className="s-water" x="0" y="690" width="1440" height="210" />
    <ellipse className="s-ripple" cx="360" cy="742" rx="26" ry="5" />
    <ellipse className="s-ripple" cx="820" cy="788" rx="34" ry="7" style={{ animationDelay: '3.7s' }} />
    <ellipse className="s-ripple" cx="1120" cy="756" rx="29" ry="6" style={{ animationDelay: '7.4s' }} />
    <ellipse className="s-ripple" cx="560" cy="852" rx="40" ry="8" style={{ animationDelay: '11.1s' }} />
    <path className="s-branch" d={RAIN_REEDS} />
  </svg>
);

/* ── FOLIAGE — a canopy overhead, leaves coming off it ────────────────────── */
const FOLIAGE_WOOD = crownRow(RIDGE_XS, 772, [26, 34, 29, 38, 31], [0, -18, 10, -8, 16, -4]);
const FOLIAGE_TREES = broadleaf(168, 812, 44) + broadleaf(1270, 806, 36) + broadleaf(1180, 818, 22);
const FOLIAGE_BRANCHES = 'M-10 108 C 180 152 316 92 470 172 M1450 132 C 1268 178 1136 108 986 186 ' +
  'M470 172 C 540 196 566 232 578 286 M986 186 C 916 214 894 248 886 300 ' +
  'M600 900 C 612 862 600 842 620 818 M646 900 C 656 866 646 848 664 828 ' +
  'M700 900 C 706 870 698 852 714 836';

export const LeavesScene = () => (
  <svg className="amb__scene" viewBox="0 0 1440 900" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <radialGradient id="ambMoon" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#EEF4D8" stopOpacity="0.5" />
        <stop offset="66%" stopColor="#D6E2B4" stopOpacity="0.28" />
        <stop offset="100%" stopColor="#B8C894" stopOpacity="0" />
      </radialGradient>
    </defs>
    <ellipse className="s-sun" cx="1178" cy="232" rx="58" ry="58" />
    <path className="s-canopy" d="M0 0 L1440 0 L1440 96 C1360 150 1300 96 1216 138 C1130 182 1064 118 976 156 C892 192 826 128 736 164 C650 198 580 134 492 168 C404 202 336 140 244 174 C158 206 84 148 0 178 Z" />
    <path className="s-branch" d={FOLIAGE_BRANCHES} />
    <path className="s-mid-tree" d={FOLIAGE_WOOD} />
    <path className="s-near" d="M0 800 C180 748 350 812 530 786 C712 758 872 820 1052 794 C1232 768 1350 810 1440 788 L1440 900 L0 900 Z" />
    <path className="s-tree" d={FOLIAGE_TREES} />
  </svg>
);

/* ── WATERFALL — a fall off a mountain at first light, with birds ───────────
   Fourth version. History, so nobody repeats it: a gorge with two black walls
   (read as a spotlight in a paper cut-out), the same gorge with softer water,
   a mountain in flat teal (still lifeless), and a generated painting (the
   owner's verdict: "not looking good"). What was missing every time was AIR —
   the drawn themes that work, Sunrise and Snowfall, are mostly sky with a
   couple of hazy ranges in it. So: a dawn sky, a warm glow low on the left,
   two hazy ranges, ONE dark mountain with the fall in its gully, a lake with
   the fall reflected in it, a few pines, and birds crossing very slowly
   (the birds live in the fx layer so they can cross the whole viewport).
   The falling water is the CSS column in ambience.css, pinned to the gully
   drawn here: x 638-804 at the base, 676-768 at the lip, y 300-700. ── */
const FALLS_TREES = pine(118, 708, 76, 22) + pine(176, 714, 52, 16) +
  pine(232, 710, 62, 19) + pine(1300, 708, 82, 24) + pine(1240, 714, 56, 17) +
  pine(1182, 711, 64, 20);

export const WaterfallScene = () => (
  <svg className="amb__scene" viewBox="0 0 1440 900" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <radialGradient id="ambFallsGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#F6D9A8" stopOpacity="0.34" />
        <stop offset="56%" stopColor="#E8B98A" stopOpacity="0.13" />
        <stop offset="100%" stopColor="#E8B98A" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="ambFallsReflect" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#DCF2F6" stopOpacity="0.09" />
        <stop offset="100%" stopColor="#DCF2F6" stopOpacity="0" />
      </linearGradient>
    </defs>
    <ellipse className="s-sunhalo" cx="286" cy="486" rx="420" ry="270" />
    <path className="s-far" d="M0 470 L130 396 L246 452 L372 372 L500 440 L636 380 L774 448 L900 386 L1030 452 L1168 392 L1300 450 L1440 404 L1440 900 L0 900 Z" />
    <path className="s-mid" d="M0 556 C180 520 300 556 452 540 C604 524 720 560 872 544 C1024 528 1160 560 1300 546 C1380 538 1416 546 1440 543 L1440 900 L0 900 Z" />
    <path className="s-near" d="M-20 704 L250 452 L392 528 L556 336 L648 392 L724 244 L812 356 L900 300 L1010 430 L1176 512 L1440 704 Z" />
    <path className="s-fore" d="M848 350 L892 350 L910 704 L832 704 Z" />
    <rect className="s-water" x="0" y="700" width="1440" height="200" />
    <rect className="s-reflect" x="840" y="700" width="62" height="96" />
    <path className="s-tree" d={FALLS_TREES} />
    <ellipse className="s-ripple" cx="870" cy="732" rx="32" ry="6" />
    <ellipse className="s-ripple" cx="850" cy="778" rx="48" ry="8" style={{ animationDelay: '3.4s' }} />
    <ellipse className="s-ripple" cx="896" cy="838" rx="62" ry="10" style={{ animationDelay: '6.8s' }} />
  </svg>
);

export const SCENES = {
  sunrise: SunriseScene,
  snow: SnowScene,
  fog: FogScene,
  sunset: SunsetScene,
  rain: RainScene,
  leaves: LeavesScene,
  waterfall: WaterfallScene,
};

export default SCENES;
